import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/apiService';
import { formatLocalDateTime } from '../../utils/dateUtils';
import { eventService, EventType } from '../../services/eventService';
import {
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  TableSortLabel,
  DialogActions,
  Snackbar,
  Alert,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Alert as MuiAlert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  SelectChangeEvent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// Define interfaces
interface AlertItem {
  id: number;
  event_type: string;
  sensor_id: number;
  sensor_name: string;
  sensor_serial_number?: string;
  alert_level?: number;
  trig_1?: number;
  trig_2?: number;
  trig_3?: number;
  trig_4?: number;
  trig_5?: number;
  trig_6?: number;
  trig_7?: number;
  trig_8?: number;
  created_at: string;
  is_acknowledged: boolean;
  acknowledged_at?: string;
  acknowledger_name?: string; // Affiché en tooltip uniquement (pas de colonne dédiée) -> non triable
  notes?: string;
  status?: string;  // real_alert | suspended | false_alert | heartbeat
  deleted_at?: string;
  deleted_by?: number;
  deleter_name?: string;
}
interface AlertListResponse {
  items: AlertItem[];
  total: number;
  page: number;
  size: number;
}
const AlertList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [sensorId, setSensorId] = useState<string>('');
  const [acknowledged, setAcknowledged] = useState<string>('all');
  // Champs de saisie de la période (non appliqués tant que le bouton
  // "Appliquer" du filtre date n'est pas cliqué - contrairement aux autres
  // filtres qui s'appliquent en direct)
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Valeurs effectivement utilisées pour la requête
  const [appliedStartDate, setAppliedStartDate] = useState<string>('');
  const [appliedEndDate, setAppliedEndDate] = useState<string>('');
  const [sensors, setSensors] = useState<{ id: number; name: string; serial_number?: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingSensors, setLoadingSensors] = useState(false);

  // Sort
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir,   setSortDir]   = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Ingecom : afficher tous les types d'alertes
  const isIngecom = user?.role === 'ingecom';
  const [showAll, setShowAll] = useState(false);
  const [alertType, setAlertType] = useState<string>('');

  // State for delete alerts dialog and notifications
  const [deleteSelectedAlertDialogOpen, setDeleteSelectedAlertDialogOpen] = useState<boolean>(false);
  const [acknowledgeDialogOpen, setAcknowledgeSelectedAlertsDialogOpen] = useState<boolean>(false);
  const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
  const [deleteAlertStatus, setDeleteAlertStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [actionMode, setActionMode] = useState<'acknowledge' | 'delete'>('acknowledge');
  const [notes, setNotes] = useState<string>('');  
    
  // Parse sensor_id from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sensorIdParam = params.get('sensor_id');
    if (sensorIdParam) {
      setSensorId(sensorIdParam);
    }
  }, [location]);
  // Fetch sensors for the dropdown
  const fetchSensors = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingSensors(true);
      // Fetch all sensors with minimal fields for the dropdown
      const response = await apiClient.get<{items: any[]}>('/sensors/?limit=100&fields=id,name,serial_number');
      setSensors(response.data.items);
    } catch (err) {
      console.error('Error fetching sensors for dropdown:', err);
      // Don't set error state to avoid disrupting the main component
    } finally {
      setLoadingSensors(false);
    }
  }, [isAuthenticated]);

  // Load sensors on component mount
  useEffect(() => {
    fetchSensors();
  }, [fetchSensors]);

  // Debounce searchText (la recherche est désormais faite côté serveur)
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400); // 400ms debounce
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [searchText]);

  // Fetch alerts data
  const fetchAlerts = useCallback(async () => {
    if (!isAuthenticated) {
      setError(t('auth.loginRequired'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build query params with trailing slash to avoid redirects
      let url = `/alerts/?skip=${page * rowsPerPage}&limit=${rowsPerPage}`;

      if (sensorId) {
        url += `&sensor_id=${sensorId}`;
      }
      if (acknowledged === 'deleted' && isIngecom) {
        url += `&deleted_only=true`;
      } else if (acknowledged !== 'all') {
        url += `&acknowledged=${acknowledged === 'yes'}`;
      }
      // Ingecom : mode étendu (vérification serveur côté backend)
      if (isIngecom && showAll) {
        url += `&show_all=true`;
        if (alertType) url += `&alert_type=${alertType}`;
      }
      url += `&sort_by=${encodeURIComponent(sortField)}&sort_dir=${sortDir}`;
      if (debouncedSearch) {
        url += `&search=${encodeURIComponent(debouncedSearch)}`;
      }
      if (appliedStartDate) {
        url += `&start_date=${encodeURIComponent(appliedStartDate)}`;
      }
      if (appliedEndDate) {
        // Rend la borne de fin inclusive (fin de journée), sans fuseau
        // horaire explicite : la comparaison SQL se fait en heure Paris
        // naïve, comme le reste des colonnes created_at.
        url += `&end_date=${encodeURIComponent(`${appliedEndDate}T23:59:59`)}`;
      }

      const response = await apiClient.get<AlertListResponse>(url);
      setAlerts(response.data.items);
      setTotalCount(response.data.total);
    } catch (err: any) {
      console.error('Error fetching alerts:', err);
      // Ensure error is a string, not an object
      const errorDetail = err.response?.data?.detail;
      setError(
        typeof errorDetail === 'object' ? JSON.stringify(errorDetail) :
        errorDetail || t('common.failedToLoad', { resource: t('alerts.list').toLowerCase() })
      );
      setAlerts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, sensorId, acknowledged, appliedStartDate, appliedEndDate, isAuthenticated, t, isIngecom, showAll, alertType, sortField, sortDir]);

  // Initial data load & when debouncedSearch, filters or sort change
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Subscribe to new alert events
  useEffect(() => {
    if (isAuthenticated) {
      // Set up event listener for new alerts
      const unsubscribe = eventService.subscribe(EventType.NEW_ALERT, () => {
        // Refresh alerts when a new alert is received
        fetchAlerts();
      });

      // Clean up subscription when component unmounts
      return () => {
        unsubscribe();
      };
    }
  }, [isAuthenticated, fetchAlerts]);

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  // Handle sensor ID change - now automatically triggers fetchAlerts via dependency array
  const handleSensorIdChange = (event: SelectChangeEvent) => {
    setSensorId(event.target.value);
    setPage(0);
  };

  // Handle acknowledged status change - now automatically triggers fetchAlerts via dependency array
  const handleAcknowledgedChange = (event: SelectChangeEvent) => {
    setAcknowledged(event.target.value);
    setPage(0);
  };

  // Champs de saisie de la période : ne déclenchent pas de recherche en
  // direct, contrairement aux autres filtres (voir handleApplyDateFilter)
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  // Applique le filtre de période (uniquement) sur clic du bouton
  const handleApplyDateFilter = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(0);
  };

  // Réinitialise tous les filtres (recherche, statut, capteur, mode Ingecom,
  // période) - contrairement à "Appliquer" qui ne concerne que la période
  const handleResetFilters = () => {
    setSearchText('');
    setSensorId('');
    setAcknowledged('all');
    setShowAll(false);
    setAlertType('');
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setPage(0);
  };

  // Handle click on alert row
  const handleAlertClick = (alertId: number) => {
    navigate(`/alerts/${alertId}`);
  };

  // Handle delete selected acknowledged alerts
  const handleDeleteSelectedAcknowledgedAlerts = async () => {
    if (selectedAlerts.length === 0) return;

    try {
      setDeleteAlertStatus('loading');

      const acknowledgedAlertIds = alerts
      .filter(alert => selectedAlerts.includes(alert.id) && alert.is_acknowledged)
      .map(alert => alert.id);

      if (acknowledgedAlertIds.length === 0) {
        throw new Error('No acknowledged alerts selected');
      }
    
      // Use direct fetch with correct API URL to handle 204 No Content response
      const responseList = await Promise.all(
        acknowledgedAlertIds.map(id =>
          fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'}/alerts/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          })
        )
      );

      const allSucceeded = responseList.every(response => response.ok);

      // Check if response is ok without attempting to parse the body
      if (allSucceeded) {
        setDeleteAlertStatus('success');
        setAlertMessage(t('sensor.deleteAcknowledgedAlertsSuccess') || 'Acknowledged selected alerts deleted successfully');
                
        // Close dialog
        setDeleteSelectedAlertDialogOpen(false);

        // Refresh selected alerts
        setSelectedAlerts([]);

        // Refresh alerts
        fetchAlerts();
      } else {
        throw new Error(`One or more deletions failed.`);
      }
    } catch (err: any) {
      console.error('Error deleting selected alerts:', err);
      setDeleteAlertStatus('error');
      setAlertMessage(t('sensor.deleteAcknowledgedAlertsError') || 'Failed to delete selected alerts');
    }
  };

  // Handle open delete selected acknowledged alerts dialog
  const handleOpenDeleteSelectedAcknowledgedAlertsDialog = () => {
    setDeleteSelectedAlertDialogOpen(true);
  };

  // Handle close delete selected acknowledged alerts dialog
  const handleCloseDeleteSelectedAcknowledgedAlertsDialog = () => {
    setDeleteSelectedAlertDialogOpen(false);
  };

  // Handle click to acknowledge selected alerts
  const handleAcknowledgeSelectedAlerts = async () => {
    try {
      setLoading(true);
      const acknowledgePromises = alerts
        .filter(alert => 
          selectedAlerts.includes(alert.id) && !alert.is_acknowledged
        )
        .map(alert => 
          apiClient.post('/alerts/acknowledge', {
            alert_id: alert.id, 
            notes: JSON.stringify({
              system_note_key: 'alerts.acknowledge_note',
              user_note: notes
            })
          })
        );
      
      await Promise.all(acknowledgePromises);

      // Empty the selected alerts after acknowledging
      setSelectedAlerts([]);
      setAcknowledgeSelectedAlertsDialogOpen(false);
      setNotes('');
      fetchAlerts();
    } catch (err: any) {
      console.error('Error acknowledging alerts:', err);
      setError(
        err.response?.data?.detail || 
        t('common.failedToLoad', { resource: t('alerts.acknowledge').toLowerCase() })
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle open delete selected acknowledged alerts dialog
  const handleAcknowledgeSelectedAlertsOpen = () => {
    setAcknowledgeSelectedAlertsDialogOpen(true);
  };

  // Handle close delete selected acknowledged alerts dialog
  const handleAcknowledgeSelectedAlertsClose = () => {
    setAcknowledgeSelectedAlertsDialogOpen(false);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchAlerts();
  };

  // Chip coloré pour le type d'alerte
  const renderTypeChip = (status?: string, deletedAt?: string) => {
    switch (status) {
      case 'real_alert':
        return <Chip label={t('alerts.types.real_alert')}  color="success" size="small" />;
      case 'suspended':
        return <Chip label={t('alerts.types.suspended')}   color="error"   size="small" />;
      case 'false_alert':
        return <Chip label={t('alerts.types.false_alert')} color="warning" size="small" />;
      case 'heartbeat':
        return <Chip label={t('alerts.types.heartbeat')}   size="small"    sx={{ bgcolor: '#FBC02D', color: '#000' }} />;
      default:
        return <Chip label={status ?? '—'} size="small" />;
    }
  };

  // Get count of unacknowledged alerts
  const unacknowledgedCount = alerts.filter(alert => !alert.is_acknowledged).length;
  // Current page of alerts, already filtered/searched/sorted/paginated server-side
  const currentList = alerts;

  // Update selected alerts based on action mode and filtering
  useEffect(() => {
    const selectableIds = alerts
      .filter(a =>
        (actionMode === "acknowledge" && !a.is_acknowledged) ||
        (actionMode === "delete" && a.is_acknowledged && !a.deleted_at)
      )
      .map(a => a.id);

    setSelectedAlerts(prev =>
      prev.filter(id => selectableIds.includes(id))
    );
  }, [actionMode, alerts]);

  if (loading && alerts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('navigation.alerts')}</Typography>
        {unacknowledgedCount > 0 && selectedAlerts.length > 0 && !alerts.find(a => selectedAlerts.includes(a.id) && a.is_acknowledged) && (
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={handleAcknowledgeSelectedAlertsOpen}
          >
            {t('alerts.acknowledge')} ({selectedAlerts.length})
          </Button>
        )}
      </Box>

      {error && (
        <MuiAlert
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {t('common.retry')}
            </Button>
          }
        >
          {error}
        </MuiAlert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            // En mobile, on évite que les filtres empilent toute la page :
            // une seule ligne, défilable horizontalement, plutôt qu'un
            // retour à la ligne pour chaque filtre.
            ...(isMobile
              ? { flexWrap: 'nowrap', overflowX: 'auto', pt: 1, pb: 1, '& > *': { flexShrink: 0 } }
              : { flexWrap: 'wrap' }),
          }}
        >
          <TextField
            placeholder={t('common.search_placeholder', { resource: (t('navigation.alerts') as string).toLowerCase() }) as string}
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            helperText={t('common.searchInAlertFields')}
          />
          
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel id="acknowledged-select-label">{t('alerts.status.label')}</InputLabel>
            <Select
              labelId="acknowledged-select-label"
              value={acknowledged}
              label={t('alerts.status.label')}
              onChange={handleAcknowledgedChange}
            >
              <MenuItem value="all">{t('alerts.status.all')}</MenuItem>
              <MenuItem value="no">{t('alerts.status.unacknowledged')}</MenuItem>
              <MenuItem value="yes">{t('alerts.status.acknowledged')}</MenuItem>
              {isIngecom && showAll && (
                <MenuItem value="deleted">{t('alerts.status.deleted')}</MenuItem>
              )}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel id="sensor-select-label">{t('alerts.sensorFilter')}</InputLabel>
            <Select
              labelId="sensor-select-label"
              value={sensorId}
              label={t('alerts.sensorFilter')}
              onChange={handleSensorIdChange}
            >
              <MenuItem value="">{t('alerts.allSensors')}</MenuItem>
              {sensors.map((sensor) => (
                <MenuItem key={sensor.id} value={sensor.id.toString()}>
                  {sensor.name} {sensor.serial_number ? `(${sensor.serial_number})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('common.dateFrom')}
            type="date"
            size="small"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: endDate || undefined }}
            sx={{ width: 170 }}
          />

          <TextField
            label={t('common.dateTo')}
            type="date"
            size="small"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
            sx={{ width: 170 }}
          />

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyDateFilter}
            >
              {t('alerts.applyDateFilter')}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
            >
              {t('common.reset')}
            </Button>
          </Stack>

          <Tooltip title={t('common.refresh')}>
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {/* Contrôles ingecom uniquement */}
          {isIngecom && (
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAll}
                    onChange={(e) => {
                      setShowAll(e.target.checked);
                      if (!e.target.checked) {
                        setAlertType('');
                        if (acknowledged === 'deleted') setAcknowledged('all');
                      }
                      setPage(0);
                    }}
                    size="small"
                  />
                }
                label={t('alerts.showAll')}
              />
              {showAll && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>{t('alerts.filterByType')}</InputLabel>
                  <Select
                    value={alertType}
                    label={t('alerts.filterByType')}
                    onChange={(e: SelectChangeEvent) => { setAlertType(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    <MenuItem value="real_alert">{t('alerts.types.real_alert')}</MenuItem>
                    <MenuItem value="suspended">{t('alerts.types.suspended')}</MenuItem>
                    <MenuItem value="false_alert">{t('alerts.types.false_alert')}</MenuItem>
                    <MenuItem value="heartbeat">{t('alerts.types.heartbeat')}</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          )}
        </Box>
      </Paper>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="action-label">{t('alerts.actionMode')}</InputLabel>
            <Select
              labelId="action-label"
              value={actionMode}
              label={t('alerts.actionMode')}
              onChange={e => setActionMode(e.target.value as 'acknowledge' | 'delete')}
            >
              <MenuItem value="acknowledge">
                <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                {t('alerts.acknowledge')}
              </MenuItem>
              {alerts.some(alert => alert.is_acknowledged) && user?.is_admin && ( // Verify if user is admin
                <MenuItem value="delete">
                  <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                  {t('common.delete')}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Tooltip title={t('alerts.selectAll')} arrow>
                    <Checkbox
                      indeterminate={
                        (() => {
                          const selectableIds = currentList
                            .filter(a =>
                              (actionMode === "acknowledge" && !a.is_acknowledged) ||
                              (actionMode === "delete" && a.is_acknowledged && !a.deleted_at)
                            )
                            .map(a => a.id);
                          const selectedCount = selectedAlerts.filter(id => selectableIds.includes(id)).length;
                          return selectedCount > 0 && selectedCount < selectableIds.length;
                        })()
                      }
                      checked={
                        (() => {
                          const selectableIds = currentList
                            .filter(a =>
                              (actionMode === "acknowledge" && !a.is_acknowledged) ||
                              (actionMode === "delete" && a.is_acknowledged && !a.deleted_at)
                            )
                            .map(a => a.id);
                          return (
                            selectableIds.length > 0 &&
                            selectableIds.every(id => selectedAlerts.includes(id))
                          );
                        })()
                      }
                      disabled={
                        (actionMode === "acknowledge" && currentList.filter(a => !a.is_acknowledged).length === 0) ||
                        (actionMode === "delete" && currentList.filter(a => a.is_acknowledged).length === 0) 
                      }
                      onChange={() => {
                        const selectableIds = currentList
                          .filter(a =>
                            (actionMode === "acknowledge" && !a.is_acknowledged) ||
                            (actionMode === "delete" && a.is_acknowledged && !a.deleted_at)
                          )
                          .map(a => a.id);
                        const allSelected = selectedAlerts.filter(id => selectableIds.includes(id)).length;
                        if (allSelected) {
                          // Unselect all selectable
                          setSelectedAlerts(selectedAlerts.filter(id => !selectableIds.includes(id)));
                        } else {
                          // Select all selectable (and keep already selected others)
                          setSelectedAlerts([
                            ...selectedAlerts.filter(id => !selectableIds.includes(id)),
                            ...selectableIds,
                          ]);
                        }
                      }}
                    />
                  </Tooltip>
                </TableCell>
                {([
                  { field: 'event_type',           label: t('common.eventType') },
                  { field: 'sensor_name',           label: t('sensors.name') },
                  { field: 'sensor_serial_number',  label: t('sensor.fields.serialNumber') },
                  { field: 'alert_level',           label: t('alerts.alertLevel') },
                  { field: 'created_at',            label: t('common.dateTime') },
                  { field: 'is_acknowledged',       label: t('common.status') },
                ] as { field: string; label: string }[]).map(({ field, label }) => (
                  <TableCell key={field} sortDirection={sortField === field ? sortDir : false}>
                    <TableSortLabel
                      active={sortField === field}
                      direction={sortField === field ? sortDir : 'asc'}
                      onClick={() => handleSort(field)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {isIngecom && showAll && (
                  <TableCell sortDirection={sortField === 'status' ? sortDir : false}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDir : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      {t('alerts.typeColumn')}
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isIngecom && showAll ? 8 : 7} align="center">
                    {loading ?
                      t('common.loading_resources', { resource: t('navigation.alerts').toLowerCase() }) :
                      searchText.trim() ?
                        t('common.noMatchingResults') :
                        t('common.no_resources_found', { resource: t('navigation.alerts').toLowerCase() })
                    }
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map((alert) => (
                  <TableRow
                    key={alert.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      backgroundColor: !alert.is_acknowledged ? 'rgba(255, 152, 0, 0.05)' : 'inherit',
                      // Lignes supprimées toujours grisées, autres non-real_alert grisées en mode étendu ingecom
                      opacity: (alert.deleted_at || (isIngecom && showAll && alert.status && alert.status !== 'real_alert')) ? 0.55 : 1,
                    }}
                    onClick={() => handleAlertClick(alert.id)}
                  >
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedAlerts.includes(alert.id)}
                      disabled={
                        (actionMode === "acknowledge" && alert.is_acknowledged) ||
                        (actionMode === "delete" && (!alert.is_acknowledged || !!alert.deleted_at))
                      }
                      onChange={e => {
                        e.stopPropagation();
                        setSelectedAlerts(prev =>
                          prev.includes(alert.id)
                            ? prev.filter(id => id !== alert.id)
                            : [...prev, alert.id]
                        );
                      }}
                    />
                  </TableCell>
                    <TableCell>{t(`eventTypes.${alert.event_type}`, t('eventTypes.default'))}</TableCell>
                    <TableCell>{alert.sensor_name}</TableCell>
                    <TableCell>{alert.sensor_serial_number || '-'}</TableCell>
                    <TableCell>
                      {alert.alert_level !== null && alert.alert_level !== undefined ? (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" display="block" fontWeight="bold">
                                {t('sensor.reboundCount')}
                              </Typography>
                              {[1,2,3,4,5,6,7,8].map(i => {
                                const val = alert[`trig_${i}` as keyof AlertItem] as number | undefined;
                                return val != null && val > 0 ? (
                                  <Typography key={i} variant="caption" display="block">
                                    T{i} : {val}
                                  </Typography>
                                ) : null;
                              })}
                            </Box>
                          }
                          arrow
                        >
                          <Chip
                            label={alert.alert_level}
                            color={alert.alert_level >= 3 ? 'error' : alert.alert_level >= 2 ? 'warning' : 'default'}
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {formatLocalDateTime(alert.created_at, i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                    </TableCell>
                    <TableCell>
                      {alert.deleted_at ? (
                        <Tooltip
                          title={
                            <Box>
                              {alert.deleter_name && (
                                <Typography variant="caption" display="block">
                                  {t('alerts.status.deleted')} {t('common.by')} {alert.deleter_name}{alert.deleted_at ? ',' : ''}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block">
                                {`${t('common.on')} ${formatLocalDateTime(alert.deleted_at, i18n.language === 'fr' ? 'fr-FR' : 'en-US')}`}
                              </Typography>
                            </Box>
                          }
                          arrow
                        >
                          <Chip
                            label={t('alerts.status.deleted')}
                            size="small"
                            sx={{ bgcolor: '#9E9E9E', color: '#fff', cursor: 'default' }}
                          />
                        </Tooltip>
                      ) : alert.is_acknowledged ? (
                        <Tooltip
                          title={
                            <Box>
                              {alert.acknowledger_name && (
                                <Typography variant="caption" display="block">
                                  {t('alerts.status.acknowledged')} {t('common.by')} {alert.acknowledger_name}{alert.acknowledged_at ? ',' : ''}
                                </Typography>
                              )}
                              {alert.acknowledged_at && (
                                <Typography variant="caption" display="block">
                                  {`${t('common.on')} ${formatLocalDateTime(alert.acknowledged_at, i18n.language === 'fr' ? 'fr-FR' : 'en-US')}`}
                                </Typography>
                              )}
                            </Box>
                          }
                          arrow
                        >
                          <Chip
                            icon={<CheckCircleOutlineIcon />}
                            label={t('alerts.status.acknowledged')}
                            color="success"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={<ErrorOutlineIcon />}
                          label={t('alerts.status.unacknowledged')}
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    {isIngecom && showAll && (
                      <TableCell>{renderTypeChip(alert.status, alert.deleted_at)}</TableCell>
                    )}
                    <TableCell align="right">
                      <Tooltip title={t('sensors.view')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAlertClick(alert.id);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {alerts.some(alert => alert.is_acknowledged) && user?.is_admin && ( // Verify if user is admin
          <Box sx={{ mt: 2, ml: 2, display: 'flex', justifyContent: 'space-between' }}>
            {selectedAlerts.some(id => alerts.find(a => a.id === id && a.is_acknowledged)) && (
              <Button 
                variant="outlined" 
                size="small"
                color="error"
                onClick={handleOpenDeleteSelectedAcknowledgedAlertsDialog}
                disabled={selectedAlerts.filter(id => alerts.find(a => a.id === id && a.is_acknowledged)).length === 0}
              >
                {t('sensor.deleteAcknowledgedAlerts')}
              </Button>
            )}
          </Box>
        )}

        {/* Acknowledge Dialog */}
        <Dialog open={acknowledgeDialogOpen} onClose={handleAcknowledgeSelectedAlertsClose} maxWidth="sm" fullWidth>
          <DialogTitle>{t('alertDetail.dialog.titleSelected')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              {t('alertDetail.dialog.acknowledgingSelected', {
                list: selectedAlerts
                  .map(id => {
                    const alert = alerts.find(a => a.id === id);
                    return alert ? `#${id} (${t(`eventTypes.${alert.event_type}`)})` : `#${id}`;
                  })
                  .join(', ')
              })}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('alertDetail.dialog.explanationSelected')}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label={t('alertDetail.dialog.notesLabel')}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('alertDetail.dialog.notesPlaceholderSelected') as string}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAcknowledgeSelectedAlertsClose}>{t('alertDetail.dialog.cancel')}</Button>
            <Button onClick={handleAcknowledgeSelectedAlerts} variant="contained" color="primary">
              {t('alertDetail.dialog.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Selected Acknowledged Alerts Dialog */}
        <Dialog open={deleteSelectedAlertDialogOpen} onClose={handleCloseDeleteSelectedAcknowledgedAlertsDialog}>
          <DialogTitle>{t('sensor.deleteAcknowledgedAlerts')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('sensor.deleteAcknowledgedAlertsConfirm')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteSelectedAcknowledgedAlertsDialog}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteSelectedAcknowledgedAlerts} 
              color="error" 
              variant="contained"
              disabled={deleteAlertStatus === 'loading'}
            >
              {deleteAlertStatus === 'loading' ? <CircularProgress size={24} /> : t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={deleteAlertStatus === 'success' || deleteAlertStatus === 'error' || !!location.state?.snackbar}
          autoHideDuration={6000}
          onClose={() => {
            setDeleteAlertStatus('idle');
            if (location.state?.snackbar) {
              navigate(location.pathname, { replace: true, state: {} });
            }
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => {
              setDeleteAlertStatus('idle');
              if (location.state?.snackbar) {
          navigate(location.pathname, { replace: true, state: {} });
              }
            }}
            severity={
              location.state?.snackbar?.severity ||
              (deleteAlertStatus === 'success' ? 'success' : 'error')
            }
            sx={{ width: '100%' }}
          >
            {location.state?.snackbar?.message || alertMessage}
          </Alert>
        </Snackbar>    
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} ${t('pagination.of')} ${count}`
          }
          getItemAriaLabel={(type) => {
            switch (type) {
              case 'first':
                return t('pagination.firstPage');
              case 'last':
                return t('pagination.lastPage');
              case 'next':
                return t('pagination.nextPage');
              case 'previous':
                return t('pagination.previousPage');
              default:
                return '';
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default AlertList;